/**
 * PHASE 3.1: Authentication Constants
 * Centralizes magic numbers and configuration values for the auth system
 */

// ===== Storage Keys =====
export const STORAGE_KEYS = {
  USER: 'anaka_user',
  ACCESS_TOKEN: 'anaka_access_token',
  REFRESH_TOKEN: 'anaka_refresh_token',
  LOGOUT_EVENT: 'anaka_logout_event',
  LAST_VALIDATION: 'anaka_last_validation',
  /** Mirrors profiles.settings.theme for instant theme before React hydrates */
  THEME_PREFERENCE: 'anaka_theme_preference',
} as const;

// ===== Timing Constants (in milliseconds) =====
export const TIMING = {
  // Validation caching
  VALIDATION_CACHE_DURATION: 5 * 60 * 1000, // 5 minutes

  // Token refresh scheduling
  TOKEN_REFRESH_BUFFER: 5 * 60 * 1000, // Refresh 5 minutes before expiry
  TOKEN_REFRESH_MIN_INTERVAL: 60 * 1000, // Minimum 1 minute between refreshes
  TOKEN_FALLBACK_EXPIRY: 15 * 60 * 1000, // 15 minutes if JWT decode fails

  // Cross-tab logout cleanup
  LOGOUT_EVENT_CLEANUP_DELAY: 2000, // 2 seconds

  // Auth success redirects
  AUTH_SUCCESS_REDIRECT_DELAY: 500, // 500ms
  AUTH_SUCCESS_REDIRECT_DELAY_WITH_USER: 2000, // 2 seconds when user already exists
  AUTH_ERROR_REDIRECT_DELAY: 3000, // 3 seconds before redirecting to error page
} as const;

// ===== API Endpoints =====
export const API_ENDPOINTS = {
  AUTH_ME: '/api/v1/auth/me',
  AUTH_LOGIN: '/api/v1/auth/login',
  AUTH_REGISTER: '/api/v1/auth/register',
  AUTH_REFRESH: '/api/v1/auth/refresh',
  AUTH_LOGOUT: '/api/v1/auth/logout',
  PROFILE_SETTINGS: '/api/v1/profile/settings',
  SOCIAL_LOGIN: (provider: string) => `/api/v1/auth/social/${provider}`,
  SUPABASE_CALLBACK: '/api/v1/auth/supabase-callback',
  MAGIC_LOGIN: '/api/v1/auth/magic-login',
  RESET_PASSWORD: '/api/v1/auth/reset-password',
} as const;

/**
 * Invite-only "Request access" footer target (spec 019, FR-020).
 * Placeholder mailto — confirm the real destination at go-live (ANA-155).
 */
export const REQUEST_ACCESS_URL = 'mailto:hello@anaka.ai?subject=Request%20access%20to%20Anaka%20Cloud';

// ===== Device/Platform Constants =====
export const DEVICE_INFO = {
  WEB_DEVICE_ID: 'web-browser',
  WEB_PLATFORM: 'web',
} as const;

// ===== Extension Detection =====
export const EXTENSION = {
  URL_PARAM: 'ext',
  URL_PARAM_VALUE: 'true',
  PATH_INDICATOR: '/extension',
  REFERRER_INDICATOR: 'extension-',
  HOME_PATH: '/',
  HOME_PATH_WITH_EXT: '/?ext=true',
} as const;

// ===== Error Messages =====
export const ERROR_MESSAGES = {
  NO_REFRESH_TOKEN: 'No refresh token available',
  TOKEN_REFRESH_FAILED: 'Token refresh failed',
  SESSION_EXPIRED: 'Session expired',
  LOGOUT_ALL_PLATFORMS: 'You have been logged out from all platforms',
  LOGOUT_ANOTHER_TAB: 'You have been logged out from another tab',
  AUTH_SESSION_EXPIRED: 'Authentication session expired',
  FAILED_TO_DECODE_TOKEN: 'Failed to decode access token',
  MUST_USE_WITHIN_PROVIDER: 'useAuth must be used within an AuthProvider',
} as const;

// ===== Public Routes (no auth/me needed) =====
/** Path prefixes that do not require session validation (guest pages, auth flows) */
export const PUBLIC_ROUTE_PREFIXES = [
  '/interview/',  // Guest invite flow
  '/demo/',       // Demo invite guest flow (unauthenticated prospect)
  '/login',       // Login / register page
  '/auth',        // OAuth callbacks, magic-link, reset-password, etc.
] as const;

export function isPublicRoute(): boolean {
  if (typeof window === 'undefined') return false;
  // Path-routed app; legacy `#/...` URLs (pre-migration bookmarks) are honored
  // until the boot shim rewrites them. An implicit auth fragment
  // (`#access_token=…`) is NOT a route — the pathname decides.
  const { pathname, hash } = window.location;
  const path = hash.startsWith('#/') ? hash.slice(1) : (pathname || '');
  return PUBLIC_ROUTE_PREFIXES.some((prefix) => path.startsWith(prefix));
}

// ===== Log Prefixes =====
export const LOG_PREFIX = {
  AUTH: '🔵 AUTH:',
  AUTH_SUCCESS: '🔵 AUTH SUCCESS:',
  TOKEN_REFRESH: '🔄 TOKEN REFRESH:',
  OAUTH: '📧 OAUTH:',
  ERROR: '🔴',
  SUCCESS: '✅',
  WARNING: '⚠️',
} as const;
